/**
 * SignupPage.tsx — /#/signup
 * Multi-step organization registration:
 * Step 1 — Choose plan
 * Step 2 — Organization profile (name, slug, industry, size, address, contact)
 * Step 3 — Billing (Stripe checkout)
 */
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

const GOLD  = '#c9a84c'
const NAVY  = '#0a1628'
const PANEL = '#0f1a2e'
const WORKER_URL = (import.meta.env.VITE_API_URL || 'https://castellan-security.com').replace(/\/$/, '')

const PLANS = [
  {
    priceId: 'price_1TQ8FWRBqieyUkRJRZZbPchb',
    tier: 'shift', name: 'Shift', price: '$49.99',
    desc: 'Essential shift management for small teams.',
    features: ['Shift scheduling', 'Equipment tracking', 'Cloud sync', 'Mobile app', 'Audit log'],
    highlight: false, available: true,
  },
  {
    priceId: 'price_1TQ8GRRBqieyUkRJBthdwGn5',
    tier: 'terrain', name: 'Terrain+NFC', price: '$149.99',
    desc: 'Adds terrain mapping and NFC checkpoints.',
    features: ['Everything in Shift', 'Terrain zone map', 'NFC checkpoints', 'Incident reports', 'Priority support'],
    highlight: true, available: false,
  },
  {
    priceId: 'price_1TQ8H9RBqieyUkRJsKSP352Q',
    tier: 'command', name: 'Command', price: '$399.99',
    desc: 'Full enterprise platform.',
    features: ['Everything in Terrain+NFC', 'Command dashboard', 'Multi-site', 'REST API', 'SLA guarantee'],
    highlight: false, available: false,
  },
]

const INDUSTRIES = [
  'Mall / Retail', 'Corporate', 'Hotel / Hospitality', 'Healthcare / Hospital',
  'Campus / University', 'Warehouse / Logistics', 'Government / Public Safety',
  'Residential / HOA', 'Events / Venues', 'Other',
]

const SIZES = ['1–10 officers', '11–25 officers', '26–50 officers', '51–100 officers', '100+ officers']

const NAV: React.CSSProperties = {
  position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100,
  background: 'rgba(10,22,40,0.95)', backdropFilter: 'blur(12px)',
  borderBottom: '1px solid rgba(201,168,76,0.15)',
  display: 'flex', alignItems: 'center', padding: '0 32px', height: 60,
  fontFamily: "'Segoe UI', system-ui, sans-serif",
}

function Label({ children }: { children: React.ReactNode }) {
  return <div style={{ fontSize: 12, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase' as const, letterSpacing: '.07em', marginBottom: 6 }}>{children}</div>
}

function Input({ value, onChange, placeholder, type = 'text', disabled = false }: {
  value: string; onChange: (v: string) => void; placeholder?: string; type?: string; disabled?: boolean
}) {
  return (
    <input
      type={type} value={value} placeholder={placeholder} disabled={disabled}
      onChange={e => onChange(e.target.value)}
      style={{ width: '100%', background: '#0a1628', border: '1px solid #1e3050', borderRadius: 8, padding: '10px 14px', color: '#f1f5f9', fontSize: 14, outline: 'none', boxSizing: 'border-box' as const, opacity: disabled ? 0.5 : 1 }}
    />
  )
}

function Select({ value, onChange, options }: { value: string; onChange: (v: string) => void; options: string[] }) {
  return (
    <select value={value} onChange={e => onChange(e.target.value)}
      style={{ width: '100%', background: '#0a1628', border: '1px solid #1e3050', borderRadius: 8, padding: '10px 14px', color: value ? '#f1f5f9' : '#475569', fontSize: 14, outline: 'none', boxSizing: 'border-box' as const }}>
      <option value="">Select…</option>
      {options.map(o => <option key={o} value={o}>{o}</option>)}
    </select>
  )
}

function StepIndicator({ step }: { step: number }) {
  const steps = ['Choose plan', 'Organization profile', 'Review & pay']
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0, marginBottom: 40 }}>
      {steps.map((s, i) => (
        <div key={s} style={{ display: 'flex', alignItems: 'center' }}>
          <div style={{ display: 'flex', flexDirection: 'column' as const, alignItems: 'center', gap: 6 }}>
            <div style={{
              width: 32, height: 32, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontWeight: 700, fontSize: 13,
              background: i + 1 <= step ? GOLD : '#1e293b',
              color: i + 1 <= step ? NAVY : '#475569',
              border: i + 1 === step ? `2px solid ${GOLD}` : '2px solid transparent',
            }}>{i + 1 < step ? '✓' : i + 1}</div>
            <div style={{ fontSize: 11, color: i + 1 <= step ? '#94a3b8' : '#334155', whiteSpace: 'nowrap' as const }}>{s}</div>
          </div>
          {i < steps.length - 1 && (
            <div style={{ width: 60, height: 2, background: i + 1 < step ? GOLD : '#1e293b', margin: '0 8px', marginBottom: 20 }} />
          )}
        </div>
      ))}
    </div>
  )
}

export default function SignupPage() {
  const navigate = useNavigate()
  const [step, setStep] = useState(1)

  // Step 1 — Plan
  const [selectedPlan, setSelectedPlan] = useState(PLANS[0].priceId)

  // Step 2 — Org profile
  const [orgName, setOrgName]       = useState('')
  const [orgSlug, setOrgSlug]       = useState('')
  const [slugEdited, setSlugEdited] = useState(false)
  const [industry, setIndustry]     = useState('')
  const [size, setSize]             = useState('')
  const [address, setAddress]       = useState('')
  const [city, setCity]             = useState('')
  const [state, setState]           = useState('')
  const [zip, setZip]               = useState('')
  const [contactName, setContactName]   = useState('')
  const [contactEmail, setContactEmail] = useState('')
  const [contactPhone, setContactPhone] = useState('')

  const [loading, setLoading] = useState(false)
  const [error, setError]     = useState('')

  const plan = PLANS.find(p => p.priceId === selectedPlan) || PLANS[0]

  function handleOrgNameChange(v: string) {
    setOrgName(v)
    if (!slugEdited) {
      setOrgSlug(v.toLowerCase().replace(/[^a-z0-9]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '').slice(0, 32))
    }
  }

  function handleSlugChange(v: string) {
    setSlugEdited(true)
    setOrgSlug(v.toLowerCase().replace(/[^a-z0-9-]/g, '').slice(0, 32))
  }

  function validateStep2() {
    if (!orgName.trim())     { setError('Organization name is required'); return false }
    if (!orgSlug.trim())     { setError('Organization URL slug is required'); return false }
    if (orgSlug.length < 3)  { setError('Slug must be at least 3 characters'); return false }
    if (!industry)           { setError('Select your industry'); return false }
    if (!size)               { setError('Select your team size'); return false }
    if (!address.trim())     { setError('Street address is required'); return false }
    if (!city.trim())        { setError('City is required'); return false }
    if (!state.trim())       { setError('State is required'); return false }
    if (!zip.trim())         { setError('ZIP code is required'); return false }
    if (!contactName.trim()) { setError('Contact name is required'); return false }
    if (!contactEmail.trim() || !/^[^@]+@[^@]+\.[^@]+$/.test(contactEmail)) { setError('Enter a valid contact email'); return false }
    return true
  }

  async function handleCheckout() {
    setError(''); setLoading(true)
    try {
      const res = await fetch(`${WORKER_URL}/api/v1/billing/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          priceId: selectedPlan,
          email: contactEmail,
          companyName: orgName,
          tenantSlug: orgSlug,
          industry, size,
          address: `${address}, ${city}, ${state} ${zip}`.trim().replace(/^,\s*/, ''),
          contactName, contactPhone,
        }),
      })
      const data = await res.json()
      if (!data.success) { setError(data.error || 'Failed to start checkout'); return }
      window.location.href = data.url
    } catch {
      setError('Network error. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div style={{ minHeight: '100vh', background: NAVY, fontFamily: "'Segoe UI', system-ui, sans-serif" }}>

      {/* Nav */}
      <nav style={NAV}>
        <div onClick={() => navigate('/')} style={{ fontSize: 20, fontWeight: 800, color: GOLD, letterSpacing: 2, flex: 1, cursor: 'pointer' }}>CASTELLAN</div>
        <div style={{ display: 'flex', gap: 20, alignItems: 'center' }}>
          <span onClick={() => navigate('/pricing')} style={{ color: '#94a3b8', cursor: 'pointer', fontSize: 14 }}>Pricing</span>
          <span onClick={() => navigate('/find-agency')} style={{ color: '#94a3b8', cursor: 'pointer', fontSize: 14 }}>Sign in</span>
        </div>
      </nav>

      <div style={{ maxWidth: 860, margin: '0 auto', padding: '100px 24px 60px' }}>

        {/* Title */}
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          <h1 style={{ fontSize: 32, fontWeight: 800, color: '#f1f5f9', marginBottom: 8 }}>Set up your organization</h1>
          <p style={{ color: '#64748b', fontSize: 15 }}>Get your security operation running in minutes.</p>
        </div>

        <StepIndicator step={step} />

        {/* ── STEP 1: Choose plan ── */}
        {step === 1 && (
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16, marginBottom: 32 }}>
              {PLANS.map(p => (
                <div
                  key={p.priceId}
                  onClick={() => p.available && setSelectedPlan(p.priceId)}
                  style={{
                    background: PANEL, borderRadius: 12, padding: '24px 20px', position: 'relative',
                    border: `${selectedPlan === p.priceId ? 2 : 1}px solid ${selectedPlan === p.priceId ? GOLD : 'rgba(201,168,76,0.12)'}`,
                    cursor: p.available ? 'pointer' : 'not-allowed',
                    opacity: p.available ? 1 : 0.5,
                    transition: 'border-color .15s',
                  }}
                >
                  {p.highlight && <div style={{ position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)', background: GOLD, color: NAVY, fontSize: 10, fontWeight: 700, padding: '3px 12px', borderRadius: 20 }}>MOST POPULAR</div>}
                  {!p.available && <div style={{ position: 'absolute', top: 12, right: 12, background: '#1e293b', color: '#475569', fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 10 }}>Coming soon</div>}
                  {selectedPlan === p.priceId && (
                    <div style={{ position: 'absolute', top: 12, right: 12, width: 20, height: 20, borderRadius: '50%', background: GOLD, color: NAVY, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700 }}>✓</div>
                  )}
                  <div style={{ color: GOLD, fontWeight: 700, fontSize: 13, textTransform: 'uppercase' as const, letterSpacing: '.05em', marginBottom: 6 }}>{p.name}</div>
                  <div style={{ fontSize: 28, fontWeight: 800, color: '#f1f5f9', marginBottom: 6 }}>{p.price}<span style={{ fontSize: 13, color: '#64748b', fontWeight: 400 }}>/mo</span></div>
                  <div style={{ fontSize: 13, color: '#64748b', marginBottom: 16 }}>{p.desc}</div>
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                    {p.features.map(f => <li key={f} style={{ fontSize: 13, color: '#94a3b8', padding: '3px 0' }}><span style={{ color: '#4ade80', marginRight: 6 }}>✓</span>{f}</li>)}
                  </ul>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={() => setStep(2)} style={{ padding: '12px 32px', background: GOLD, color: NAVY, border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 15, cursor: 'pointer' }}>
                Continue →
              </button>
            </div>
          </div>
        )}

        {/* ── STEP 2: Organization profile ── */}
        {step === 2 && (
          <div style={{ background: PANEL, border: '1px solid rgba(201,168,76,0.12)', borderRadius: 16, padding: '32px 36px' }}>
            {error && <div style={{ background: 'rgba(239,68,68,0.08)', color: '#f87171', padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 20 }}>{error}</div>}

            {/* Organization details */}
            <div style={{ marginBottom: 28 }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#f1f5f9', marginBottom: 16, paddingBottom: 8, borderBottom: '1px solid rgba(255,255,255,0.06)' }}>Organization details</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div style={{ gridColumn: '1 / -1' }}>
                <Label>Organization name *</Label>
                <Input value={orgName} onChange={handleOrgNameChange} placeholder="Acme Security Group" />
                  <div style={{ fontSize: 11, color: '#334155', marginTop: 4 }}>The full display name shown to your officers and in reports.</div>
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                <Label>Organization ID (slug) *</Label>
                <div style={{ display: 'flex', alignItems: 'center', background: '#0a1628', border: '1px solid #1e3050', borderRadius: 8, overflow: 'hidden' }}>
                <span style={{ padding: '10px 12px', color: '#334155', fontSize: 13, whiteSpace: 'nowrap' as const, borderRight: '1px solid #1e3050' }}>ID:</span>
                <input
                value={orgSlug} onChange={e => handleSlugChange(e.target.value)}
                  placeholder="acme-security"
                  style={{ flex: 1, background: 'transparent', border: 'none', outline: 'none', color: '#f1f5f9', fontSize: 14, padding: '10px 14px' }}
                  />
                </div>
                <div style={{ fontSize: 11, color: '#334155', marginTop: 4 }}>
                  Used to find your organization on the sign-in page. Your app URL:&nbsp;
                    {orgSlug ? <span style={{ color: GOLD }}>https://{orgSlug}.castellan-security.com</span> : <span>https://your-id.castellan-security.com</span>}
                  </div>
                </div>
                <div>
                  <Label>Industry *</Label>
                  <Select value={industry} onChange={setIndustry} options={INDUSTRIES} />
                </div>
                <div>
                  <Label>Team size *</Label>
                  <Select value={size} onChange={setSize} options={SIZES} />
                </div>
              </div>
            </div>

            {/* Location */}
            <div style={{ marginBottom: 28 }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#f1f5f9', marginBottom: 16, paddingBottom: 8, borderBottom: '1px solid rgba(255,255,255,0.06)' }}>Location</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div style={{ gridColumn: '1 / -1' }}>
                  <Label>Street address *</Label>
                  <Input value={address} onChange={setAddress} placeholder="123 Main Street" />
                </div>
                <div>
                  <Label>City *</Label>
                  <Input value={city} onChange={setCity} placeholder="Springfield" />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div>
                    <Label>State *</Label>
                    <Input value={state} onChange={setState} placeholder="VA" />
                  </div>
                  <div>
                    <Label>ZIP *</Label>
                    <Input value={zip} onChange={setZip} placeholder="12345" />
                  </div>
                </div>
              </div>
            </div>

            {/* Primary contact */}
            <div style={{ marginBottom: 28 }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#f1f5f9', marginBottom: 16, paddingBottom: 8, borderBottom: '1px solid rgba(255,255,255,0.06)' }}>Primary contact</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div style={{ gridColumn: '1 / -1' }}>
                  <Label>Full name *</Label>
                  <Input value={contactName} onChange={setContactName} placeholder="Jane Smith" />
                </div>
                <div>
                  <Label>Email address *</Label>
                  <Input value={contactEmail} onChange={setContactEmail} placeholder="you@yourorg.com" type="email" />
                </div>
                <div>
                  <Label>Phone number</Label>
                  <Input value={contactPhone} onChange={setContactPhone} placeholder="+1 (555) 000-0000" type="tel" />
                </div>
              </div>
            </div>

            {/* Selected plan summary */}
            <div style={{ background: 'rgba(201,168,76,0.05)', border: '1px solid rgba(201,168,76,0.15)', borderRadius: 10, padding: '14px 18px', marginBottom: 24, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontSize: 12, color: '#64748b', fontWeight: 600, textTransform: 'uppercase' as const, letterSpacing: '.06em' }}>Selected plan</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: GOLD, marginTop: 2 }}>{plan.name} — {plan.price}/mo</div>
              </div>
              <button onClick={() => { setStep(1); setError('') }} style={{ fontSize: 13, color: '#64748b', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>Change</button>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <button onClick={() => { setStep(1); setError('') }} style={{ fontSize: 14, color: '#64748b', background: 'none', border: 'none', cursor: 'pointer' }}>← Back</button>
              <button
                onClick={() => { if (validateStep2()) { setError(''); setStep(3) } }}
                style={{ padding: '12px 32px', background: GOLD, color: NAVY, border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 15, cursor: 'pointer' }}
              >
                Continue →
              </button>
            </div>
          </div>
        )}

        {/* ── STEP 3: Review & pay ── */}
        {step === 3 && (
          <div style={{ background: PANEL, border: '1px solid rgba(201,168,76,0.12)', borderRadius: 16, padding: '32px 36px' }}>
            {error && <div style={{ background: 'rgba(239,68,68,0.08)', color: '#f87171', padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 20 }}>{error}</div>}

            <div style={{ fontSize: 15, fontWeight: 700, color: '#f1f5f9', marginBottom: 20, paddingBottom: 8, borderBottom: '1px solid rgba(255,255,255,0.06)' }}>Review your organization</div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0, marginBottom: 24 }}>
              {[
                ['Organization name', orgName],
                ['Organization ID', orgSlug],
                ['App URL', `${orgSlug}.castellan-security.com`],
                ['Industry', industry],
                ['Team size', size],
                ['Contact', contactName],
                ['Email', contactEmail],
                ...(contactPhone ? [['Phone', contactPhone]] : []),
                ...(address ? [['Address', `${address}${city ? ', ' + city : ''}${state ? ', ' + state : ''} ${zip}`.trim()]] : []),
              ].map(([label, value]) => (
                <div key={label} style={{ padding: '10px 0', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                  <div style={{ fontSize: 11, color: '#475569', fontWeight: 600, textTransform: 'uppercase' as const, letterSpacing: '.06em' }}>{label}</div>
                  <div style={{ fontSize: 14, color: '#cbd5e1', marginTop: 3 }}>{value}</div>
                </div>
              ))}
            </div>

            <div style={{ background: 'rgba(201,168,76,0.05)', border: '1px solid rgba(201,168,76,0.15)', borderRadius: 10, padding: '16px 20px', marginBottom: 28 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: GOLD }}>{plan.name}</div>
                  <div style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>{plan.tier === 'shift' ? '14-day free trial included — no charge today' : 'Billed monthly'}</div>
                </div>
                <div style={{ fontSize: 24, fontWeight: 800, color: '#f1f5f9' }}>{plan.price}<span style={{ fontSize: 13, color: '#64748b', fontWeight: 400 }}>/mo</span></div>
              </div>
            </div>

            <p style={{ fontSize: 12, color: '#334155', marginBottom: 24, lineHeight: 1.6 }}>
              By clicking "Proceed to payment" you agree to our <span onClick={() => navigate('/terms')} style={{ color: GOLD, cursor: 'pointer' }}>Terms of Service</span> and <span onClick={() => navigate('/privacy')} style={{ color: GOLD, cursor: 'pointer' }}>Privacy Policy</span>. You'll be taken to Stripe's secure checkout to complete payment.
            </p>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <button onClick={() => { setStep(2); setError('') }} style={{ fontSize: 14, color: '#64748b', background: 'none', border: 'none', cursor: 'pointer' }}>← Back</button>
              <button
                onClick={handleCheckout}
                disabled={loading}
                style={{ padding: '13px 36px', background: loading ? '#64748b' : `linear-gradient(135deg, ${GOLD}, #d97706)`, color: NAVY, border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 16, cursor: loading ? 'not-allowed' : 'pointer' }}
              >
                {loading ? 'Redirecting to Stripe…' : 'Proceed to payment →'}
              </button>
            </div>
          </div>
        )}

        <p style={{ textAlign: 'center', color: '#334155', fontSize: 12, marginTop: 32 }}>
          Already have an account? <span onClick={() => navigate('/find-agency')} style={{ color: GOLD, cursor: 'pointer' }}>Sign in →</span>
        </p>
      </div>
    </div>
  )
}
